/* =========================================================
   AF — Showcase Gallery (seed → cache → feed → GraphQL)
   File: assets/af-showcase-infinite-grid.js

   Render priority (fastest to slowest):
   1. Inline seed JSON (from Liquid, zero network) → instant first paint
   2. localStorage cache                           → instant on return visits
   3. XML product feed (feed.aromatica.one)        → full catalog, background
   4. Storefront GraphQL fallback                  → if feed fails
   ========================================================= */
(() => {
  "use strict";

  const FEED_URL         = "https://feed.aromatica.one/products.xml";
  // Bounds the wait for the feed's response headers. Kept at 10s on purpose:
  // the feed (any size in stock) and the GraphQL fallback (every size in
  // stock) return different product lists, so failing over sooner would
  // change WHAT slow-connection shoppers see, not just how fast. The fallback
  // is now ~5 requests, so a cold feed still resolves ~6s sooner than before.
  // feed.aromatica.one answers in under 1s (~25MB of XML, ~1.5MB gzipped);
  // the old Cloudflare worker feed took ~17s.
  const FETCH_TIMEOUT_MS = 10000;
  const API_PATH         = "/api/2026-01/graphql.json";
  // Storefront max per page. ~720 in-stock products = 5 round trips instead
  // of ~45 at 24/page. Query cost at 250 is ~200, well under the 1,000 limit.
  const GQL_PAGE_SIZE    = 250;
  // A cache younger than this skips the background re-download (repeat visit
  // minutes later). Kept short, not the 6h cache TTL, so sold-out products
  // still drop out of the gallery soon.
  const REFRESH_MIN      = 20;

  const roots = document.querySelectorAll("[data-af-sg]");
  if (!roots.length) return;

  roots.forEach((root) => {
    if (!root) return;
    if (root.dataset.afInit === "1") return;
    root.dataset.afInit = "1";

    const groupsMount = root.querySelector("[data-af-sg-groups]");
    const status      = root.querySelector("[data-af-sg-status]");
    const statusText  = status?.querySelector(".af-sg__statusText");
    const err         = root.querySelector("[data-af-sg-err]");
    const end         = root.querySelector("[data-af-sg-end]");
    if (!groupsMount || !status || !err || !end) return;

    const showTitle = root.dataset.showTitle === "true";
    const minTile   = Math.max(200, Math.min(520, parseInt(root.dataset.minTile || "320", 10)));
    const cacheMin  = Math.max(0, Math.min(10080, parseInt(root.dataset.cacheMin || "360", 10)));
    const sortKey   = (root.dataset.sort || "CREATED_AT").trim();
    const reverse   = root.dataset.reverse === "true";
    const seedId    = root.dataset.seedId || "";

    root.style.setProperty("--sg-min-tile", minTile + "px");

    const CACHE_KEY = `af_sg_feed_v1:${location.hostname}`;

    // ── Helpers ───────────────────────────────────────────

    const withWidth = (url, w) => {
      if (!url) return "";
      if (url.includes("cdn.shopify.com")) {
        const sep = url.includes("?") ? "&" : "?";
        return url + sep + "width=" + w;
      }
      return url;
    };

    const setState = (mode, detail) => {
      err.hidden    = mode !== "error";
      end.hidden    = mode !== "end";
      status.hidden = mode === "idle" || mode === "end";
      if (statusText) {
        statusText.textContent =
          mode === "loading" ? "Loading" :
          mode === "error"   ? ("Error" + (detail ? ": " + detail : "")) : "";
      }
    };

    // ── Vendor groups (A→Z) ───────────────────────────────

    const vendorCollator = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });
    const titleCollator  = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });

    let groupMap   = new Map();
    let groupOrder = [];

    const ensureGroup = (vendorName) => {
      const v = (vendorName || "Unknown").trim() || "Unknown";
      if (groupMap.has(v)) return groupMap.get(v);

      const wrap = document.createElement("section");
      wrap.className      = "af-sg__grp";
      wrap.dataset.vendor = v;

      const head = document.createElement("div");
      head.className = "af-sg__grpHead";

      const title = document.createElement("div");
      title.className   = "af-sg__grpTitle";
      title.textContent = v;
      head.appendChild(title);

      const grid = document.createElement("div");
      grid.className = "af-sg__masonry";
      grid.setAttribute("aria-label", v + " products");

      wrap.appendChild(head);
      wrap.appendChild(grid);

      let inserted = false;
      for (let i = 0; i < groupOrder.length; i++) {
        const cur = groupOrder[i];
        if (vendorCollator.compare(v, cur) < 0) {
          const curWrap = groupMap.get(cur)?.wrap;
          if (curWrap) groupsMount.insertBefore(wrap, curWrap);
          groupOrder.splice(i, 0, v);
          inserted = true;
          break;
        }
      }
      if (!inserted) {
        groupsMount.appendChild(wrap);
        groupOrder.push(v);
      }

      groupMap.set(v, { wrap, grid });
      return groupMap.get(v);
    };

    // ── Masonry layout ────────────────────────────────────

    const pendingGrids = new Set();
    const pendingTiles = new Set();
    let layoutRaf = 0;

    const getGridMetrics = (grid) => {
      const cs  = getComputedStyle(grid);
      const row = parseFloat(cs.getPropertyValue("grid-auto-rows")) || 8;
      const gap = parseFloat(cs.getPropertyValue("gap")) || 0;
      return { row, gap };
    };

    // Measure only — returns the tile's row span. flushLayouts writes the spans
    // after all reads, so a batch of tiles costs one layout, not one per tile.
    const measureSpan = (item, metrics) => {
      const { row, gap } = metrics;
      const frame = item.querySelector(".af-sg__frame") || item;
      let h       = frame.getBoundingClientRect().height;

      // Reserve the correct height BEFORE the image loads, using the real
      // Shopify image aspect ratio carried in the seed/GraphQL data. Without
      // this the frame measures as a ~pad-only stub until the image downloads,
      // then the tile pops to its real height (layout shift). We compute the
      // height the frame will settle to — inner width / ratio, clamped to the
      // same max-image-height + object-fit:contain the CSS applies — so the
      // very first layout pass reserves the right box. Only used while the
      // image is still loading; once loaded we trust the measured height.
      const img = item.querySelector("img");
      const ar  = parseFloat(item.dataset.ar || "0");
      if (ar > 0 && (!img || !img.complete || !img.naturalHeight)) {
        const cs      = getComputedStyle(frame);
        const padX    = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
        const padY    = (parseFloat(cs.paddingTop)  || 0) + (parseFloat(cs.paddingBottom) || 0);
        const maxImgH = parseFloat(cs.getPropertyValue("--sg-max-img-h")) || 560;
        const innerW  = Math.max(0, frame.getBoundingClientRect().width - padX);
        if (innerW > 0) {
          const reserved = Math.min(innerW / ar, maxImgH) + padY;
          if (reserved > 0) h = reserved;
        }
      }

      return Math.max(1, Math.ceil((h + gap) / (row + gap)));
    };

    // Whole grids (new tiles, resize) re-measure every tile; an image load
    // re-measures only its own tile — the other tiles' heights can't change.
    const flushLayouts = () => {
      layoutRaf = 0;
      const metrics = new Map();
      const metricsFor = (g) => {
        if (!metrics.has(g)) metrics.set(g, getGridMetrics(g));
        return metrics.get(g);
      };
      const spans = [];
      pendingGrids.forEach((g) => {
        if (!g.isConnected) return;
        g.querySelectorAll(".af-sg__tile").forEach((it) => spans.push([it, measureSpan(it, metricsFor(g))]));
      });
      pendingTiles.forEach((it) => {
        const g = it.parentNode;
        if (!it.isConnected || pendingGrids.has(g)) return;
        spans.push([it, measureSpan(it, metricsFor(g))]);
      });
      pendingGrids.clear();
      pendingTiles.clear();
      for (const [it, span] of spans) it.style.gridRowEnd = "span " + span;
    };

    const requestFlush = () => {
      if (layoutRaf) return;
      layoutRaf = requestAnimationFrame(flushLayouts);
    };

    const scheduleLayout = (grid) => {
      if (!grid) return;
      pendingGrids.add(grid);
      requestFlush();
    };

    const scheduleTile = (tile) => {
      pendingTiles.add(tile);
      requestFlush();
    };

    const scheduleLayoutAll = () => {
      for (const v of groupOrder) {
        const g = groupMap.get(v);
        if (g?.grid) scheduleLayout(g.grid);
      }
    };

    let resizeRaf = 0;
    window.addEventListener("resize", () => {
      if (resizeRaf) return;
      resizeRaf = requestAnimationFrame(() => { resizeRaf = 0; scheduleLayoutAll(); });
    }, { passive: true });

    // ── Tile builder ──────────────────────────────────────

    const insertTileSorted = (grid, tile) => {
      const t    = (tile.dataset.titleKey || "").trim();
      const kids = grid.children;
      for (let i = 0; i < kids.length; i++) {
        const k = kids[i].dataset?.titleKey || "";
        if (k && titleCollator.compare(t, k) < 0) {
          grid.insertBefore(tile, kids[i]);
          return;
        }
      }
      grid.appendChild(tile);
    };

    let tileIndex = 0;

    // Seed products by handle (filled at boot). The cache / full-catalog render
    // reuses their image URLs — the exact files the Liquid preloaded and the
    // seed already downloaded — instead of fetching the same photo again under
    // its cdn.shopify.com URL. Also fills the aspect ratio the XML feed lacks.
    const seedImgs = new Map();

    const makeTile = (p) => {
      const s = seedImgs.get(p.handle);
      if (s) p = { ...p, img: s.img, img540: s.img540, ar: p.ar || s.ar };

      const a = document.createElement("a");
      a.className = "af-sg__tile";
      a.href      = "/products/" + p.handle;
      a.setAttribute("aria-label", p.title || "Product");
      a.dataset.titleKey = (p.title || "").trim();

      // Real image aspect ratio (width/height) when known — lets measureSpan
      // reserve the tile height before the image loads. Absent for the XML
      // feed (Google Merchant has no dims); those fall back to on-load measure.
      const arNum = parseFloat(p.ar);
      if (arNum > 0) a.dataset.ar = String(arNum);

      const frame = document.createElement("div");
      frame.className = "af-sg__frame";

      if (p.img) {
        const img     = document.createElement("img");
        img.className = "af-sg__img";
        img.alt       = (p.alt || p.title || "").trim();
        const isEager = tileIndex < 10;
        img.loading   = isEager ? "eager" : "lazy";
        img.decoding  = "async";
        if (isEager) img.fetchPriority = "high";
        tileIndex++;
        const srcUrl = p.img.includes("width=") ? p.img : withWidth(p.img, 360);
        const srcUrl540 = p.img540 || withWidth(p.img, 540);
        img.src       = srcUrl;
        img.srcset    = srcUrl + " 360w, " + srcUrl540 + " 540w";
        img.sizes     = "(max-width: 749px) 48vw, (max-width: 1200px) 33vw, 360px";
        frame.appendChild(img);
      }

      if (showTitle && p.title) {
        const cap     = document.createElement("div");
        cap.className = "af-sg__cap";
        cap.textContent = p.title;
        frame.appendChild(cap);
      }

      a.appendChild(frame);
      return a;
    };

    const resetDOM = () => {
      groupsMount.innerHTML = "";
      groupMap   = new Map();
      tileIndex  = 0;
      groupOrder = [];
      renderGen++;
    };

    let renderGen = 0;

    const renderAll = (products) => {
      const myGen = ++renderGen;

      // Group products by vendor
      const vendorToProducts = new Map();
      for (const p of products) {
        const v = (p.vendor || "Unknown").trim() || "Unknown";
        if (!vendorToProducts.has(v)) vendorToProducts.set(v, []);
        vendorToProducts.get(v).push(p);
      }

      // Sort vendor keys alphabetically
      const sortedVendors = [...vendorToProducts.keys()].sort(
        (a, b) => vendorCollator.compare(a, b)
      );

      const renderGroup = (vendor) => {
        const items = vendorToProducts.get(vendor);
        const group = ensureGroup(vendor);
        const grid  = group?.grid;
        if (!grid || !items) return;

        // Build tiles in their on-screen (title) order, so the first ones
        // created — the eager, high-priority images — are the ones painted
        // first, matching the Liquid preload. Final DOM order is unchanged.
        const tiles = items
          .slice()
          .sort((a, b) => titleCollator.compare((a.title || "").trim(), (b.title || "").trim()))
          .map(p => makeTile(p));

        for (const tile of tiles) {
          insertTileSorted(grid, tile);
          const img = tile.querySelector("img");
          if (img) {
            if (img.complete && img.naturalHeight > 0) {
              scheduleLayout(grid);
            } else {
              img.addEventListener("load",  () => scheduleTile(tile), { once: true });
              img.addEventListener("error", () => scheduleTile(tile), { once: true });
            }
          }
        }

        scheduleLayout(grid);
      };

      // First 2 vendors sync (LCP), rest streamed one per frame
      const syncCount = Math.min(2, sortedVendors.length);
      for (let s = 0; s < syncCount; s++) renderGroup(sortedVendors[s]);

      let i = syncCount;
      const renderNext = () => {
        if (myGen !== renderGen) return; // a newer render pass has started
        if (i >= sortedVendors.length) return;
        renderGroup(sortedVendors[i++]);
        requestAnimationFrame(renderNext);
      };
      if (i < sortedVendors.length) requestAnimationFrame(renderNext);
    };

    // ── Cache ─────────────────────────────────────────────

    const now = () => Date.now();

    const loadCache = () => {
      if (!cacheMin) return null;
      try {
        const raw  = localStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (!data?.t || !Array.isArray(data.products)) return null;
        data.products = data.products.filter((p) => p && typeof p === "object");
        const age   = now() - data.t;
        const fresh = age >= 0 && age <= Math.min(cacheMin, REFRESH_MIN) * 60 * 1000;
        return { data, fresh };
      } catch (e) { return null; }
    };

    const saveCache = (products) => {
      if (!cacheMin) return;
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ t: now(), products }));
      } catch (e) {}
    };

    // ── Inline seed (synchronous, zero network) ───────────

    const loadSeed = () => {
      if (!seedId) return null;
      try {
        const el = document.getElementById(seedId);
        if (!el) return null;
        const data = JSON.parse(el.textContent);
        if (!Array.isArray(data) || !data.length) return null;
        return data;
      } catch (e) { return null; }
    };

    // ── PRIMARY: XML feed ─────────────────────────────────

    const fetchWithTimeout = (url, ms) => {
      const ctrl  = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), ms);
      return fetch(url, { signal: ctrl.signal }).finally(() => clearTimeout(timer));
    };

    const parseFeed = (xmlText) => {
      const parser = new DOMParser();
      const doc    = parser.parseFromString(xmlText, "application/xml");
      if (doc.querySelector("parsererror")) throw new Error("XML parse error");

      const ns    = "http://base.google.com/ns/1.0";
      const items = doc.querySelectorAll("item");
      const seen  = new Map();

      items.forEach((item) => {
        const get = (tag) =>
          item.getElementsByTagNameNS(ns, tag)[0]?.textContent?.trim() || "";

        if (get("availability") !== "in_stock") return;

        const groupId = get("item_group_id");
        if (!groupId || seen.has(groupId)) return;

        const link   = get("link");
        const handle = link.split("/products/")[1]?.split("?")[0] || "";
        if (!handle) return;

        seen.set(groupId, {
          groupId, handle,
          title:  get("title"),
          vendor: get("brand") || "Unknown",
          img:    get("image_link"),
          alt:    get("title"),
        });
      });

      return [...seen.values()];
    };

    const fetchFeed = async () => {
      const res = await fetchWithTimeout(FEED_URL, FETCH_TIMEOUT_MS);
      if (!res.ok) throw new Error("HTTP " + res.status);
      const text     = await res.text();
      const products = parseFeed(text);
      if (!products.length) throw new Error("empty feed");
      return products;
    };

    // ── FALLBACK: Storefront GraphQL ──────────────────────

    const GQL_QUERY = `
      query Showcase($first: Int!, $after: String, $sortKey: ProductSortKeys!, $reverse: Boolean!) {
        products(first: $first, after: $after, sortKey: $sortKey, reverse: $reverse) {
          pageInfo { hasNextPage endCursor }
          nodes {
            id title handle vendor
            featuredImage { url altText width height }
            variants(first: 50) {
              pageInfo { hasNextPage }
              nodes { availableForSale }
            }
          }
        }
      }
    `;

    const isStrictInStock = (p) => {
      const v = p?.variants;
      if (!v || !Array.isArray(v.nodes)) return false;
      if (v?.pageInfo?.hasNextPage) return false;
      return v.nodes.every((x) => x?.availableForSale === true);
    };

    const numId = (gid) => gid ? String(gid).split("/").pop() : "";

    const fetchGraphQL = async () => {
      const products = [];
      const seen     = new Set();
      let cursor     = null;
      let hasNext    = true;

      while (hasNext) {
        const res = await fetch(API_PATH, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            query: GQL_QUERY,
            variables: { first: GQL_PAGE_SIZE, after: cursor, sortKey, reverse }
          })
        });

        if (!res.ok) throw new Error("GraphQL HTTP " + res.status);
        const json = await res.json();
        if (json.errors) throw new Error("GraphQL error");

        const nodes    = json?.data?.products?.nodes || [];
        const pageInfo = json?.data?.products?.pageInfo;

        for (const p of nodes) {
          const id = numId(p?.id);
          if (!id || seen.has(id) || !isStrictInStock(p)) continue;
          seen.add(id);
          const fw = p?.featuredImage?.width;
          const fh = p?.featuredImage?.height;
          products.push({
            groupId: id,
            title:   p.title || "",
            handle:  p.handle || "",
            vendor:  p.vendor || "Unknown",
            img:     p?.featuredImage?.url || "",
            ar:      (fw && fh) ? (fw / fh) : 0,
            alt:     p?.featuredImage?.altText || p.title || "",
          });
        }

        cursor  = pageInfo?.endCursor || null;
        hasNext = !!pageInfo?.hasNextPage && !!cursor; // no cursor → stop, never re-fetch page 1
      }

      if (!products.length) throw new Error("GraphQL returned 0 products");
      return products;
    };

    // ── Background full-catalog fetch ─────────────────────
    // Runs after seed/cache has already painted — fetches full
    // catalog and re-renders if the product set has changed.

    const currentHandles = () => new Set(
      Array.from(groupsMount.querySelectorAll(".af-sg__tile"))
        .map(a => a.getAttribute("href")?.split("/products/")[1]?.split("?")[0])
        .filter(Boolean)
    );

    const fetchFullCatalog = async () => {
      let products = null;

      try {
        products = await fetchFeed();
      } catch (feedErr) {
        console.warn("[AF Gallery] Feed failed, trying GraphQL:", feedErr.message);
        try {
          products = await fetchGraphQL();
        } catch (gqlErr) {
          console.warn("[AF Gallery] GraphQL also failed:", gqlErr.message);
          return; // already showing seed/cache — silent failure is fine
        }
      }

      if (!products?.length) return;

      saveCache(products);

      // Re-render when the product handle set differs — catches adds,
      // removes, and stock changes that net to a small count delta.
      const nowIds    = currentHandles();
      const nextIds   = new Set(products.map(p => p.handle).filter(Boolean));
      const hasAdd    = [...nextIds].some(h => !nowIds.has(h));
      const hasRemove = [...nowIds].some(h => !nextIds.has(h));
      if (hasAdd || hasRemove) {
        resetDOM();
        renderAll(products);
      }

      setState("end");
    };

    // ── Boot ──────────────────────────────────────────────

    const seed = loadSeed();
    if (seed) for (const p of seed) if (p?.handle && p.img) seedImgs.set(p.handle, p);

    // A reload is the shopper asking for fresh data — it always refreshes.
    const isReload = (() => {
      try {
        const nav = performance.getEntriesByType("navigation")[0];
        if (nav) return nav.type === "reload";
        return !!(performance.navigation && performance.navigation.type === 1); // older iOS WebKit
      }
      catch (e) { return false; }
    })();

    // Priority 1: localStorage cache (instant)
    const cached = loadCache();
    if (cached?.data?.products?.length) {
      renderAll(cached.data.products);
      setState("end");
      // Refresh in background so new listings and sell-outs are picked up by
      // the handle-diff check in fetchFullCatalog — skipped only while the
      // cache is under REFRESH_MIN old (and never on a reload).
      if (!cached.fresh || isReload) requestAnimationFrame(() => fetchFullCatalog());
      return;
    }

    // Priority 2: inline seed (instant, zero network)
    if (seed?.length) {
      renderAll(seed);
      setState("idle"); // hide spinner — content is visible
      // Fetch full catalog in background
      requestAnimationFrame(() => fetchFullCatalog().then(() => setState("end")));
      return;
    }

    // Priority 3: no seed, no cache — fetch and show loading
    setState("loading");
    fetchFullCatalog().then(() => {
      if (!groupsMount.querySelector(".af-sg__tile")) {
        setState("error", "No products found");
      }
    });

  });
})();